/**
 * GraphQL Error Codes and utilities for error handling
 */
import { ErrorCodes } from "@ai-teach/types";
import type { ErrorCode } from "@ai-teach/types";

/**
 * Type-safe helper to check if a GraphQL error has a specific error code
 *
 * @param error - The error object (can be ApolloError, GraphQLError, or Error)
 * @param errorCode - The error code to check for
 * @returns true if the error has the specified error code
 *
 * @example
 * try {
 *   await updateProject(id, input);
 * } catch (error) {
 *   if (hasGraphQLErrorCode(error, ErrorCodes.CANNOT_UPDATE_LINKED_COPY)) {
 *     // Handle linked copy error
 *   }
 * }
 */
/** The server's own GraphQL error message (not a raw transport/500 Error), for showing the specific reason. */
export function getGraphQLErrorMessage(error: unknown): string | undefined {
  if (error && typeof error === "object" && "errors" in error) {
    const errors = (error as { errors?: Array<{ message?: string }> }).errors;
    return errors?.find((err) => err?.message)?.message;
  }
  if (error && typeof error === "object" && "message" in error && "extensions" in error) {
    const message = (error as { message?: string }).message;
    return typeof message === "string" ? message : undefined;
  }
  return undefined;
}

export function hasGraphQLErrorCode(error: unknown, errorCode: ErrorCode): boolean {
  if (error && typeof error === "object") {
    // Check if there's an errors array (CombinedGraphQLErrors structure)
    if ("errors" in error) {
      const errors = (error as { errors?: Array<{ extensions?: { code?: string } }> }).errors;

      return errors?.some((err) => err?.extensions?.code === errorCode) ?? false;
    }

    // Fallback: Check if extensions exists at the top level
    if ("extensions" in error) {
      const extensions = (error as { extensions?: { code?: string } }).extensions;
      return extensions?.code === errorCode;
    }
  }

  return false;
}

/** One blocking/warning issue for a single grammar-form slide, as reported by the server's
 * `GRAMMAR_FORM_NOT_PUBLISHABLE` error extension (mirrors `GrammarAuthoringIssue` from `@ai-teach/utils`). */
export interface GrammarFormValidationIssue {
  code: string;
  severity: "error" | "warning";
  message: string;
  line: number | null;
}

export interface GrammarFormValidationIssues {
  slideId: string;
  issues: GrammarFormValidationIssue[];
}

/**
 * Extracts the `extensions.validationIssues` payload from a `GRAMMAR_FORM_NOT_PUBLISHABLE` GraphQL
 * error (thrown by `publishGrammarFormSlides`), so callers can surface the server's actual blocking
 * issues instead of a generic failure toast.
 *
 * @returns the per-slide validation issues, or `undefined` if the error doesn't carry any.
 */
export function getGrammarFormValidationIssues(
  error: unknown
): GrammarFormValidationIssues[] | undefined {
  if (!error || typeof error !== "object") return undefined;

  const extractFrom = (extensions: unknown): GrammarFormValidationIssues[] | undefined => {
    if (!extensions || typeof extensions !== "object") return undefined;
    const issues = (extensions as { validationIssues?: GrammarFormValidationIssues[] })
      .validationIssues;
    return Array.isArray(issues) ? issues : undefined;
  };

  if ("errors" in error) {
    const errors = (error as { errors?: Array<{ extensions?: unknown }> }).errors;
    for (const err of errors ?? []) {
      const issues = extractFrom(err?.extensions);
      if (issues) return issues;
    }
    return undefined;
  }

  if ("extensions" in error) {
    return extractFrom((error as { extensions?: unknown }).extensions);
  }

  return undefined;
}

/**
 * Renders the server's grammar-form validation issues as a short, readable summary suitable for a
 * toast, e.g. "Grammar focus is required; Word focus has 2 words, max is 1."
 */
export function summarizeGrammarFormValidationIssues(
  validationIssues: GrammarFormValidationIssues[]
): string {
  const messages = validationIssues.flatMap((entry) => entry.issues.map((issue) => issue.message));
  return [...new Set(messages)].join("; ");
}
